import { zodResolver } from '@hookform/resolvers/zod';
import { loginRequestSchema } from '@rfz/contracts';
import { Button, Input } from '@rfz/ui';
import { useMutation } from '@tanstack/react-query';
import { Check, Eye, EyeOff, Loader2, Plus } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';
import { login, type CurrentUser } from './api.js';

type LoginInput = z.input<typeof loginRequestSchema>;
type ActiveField = 'username' | 'password' | null;
type LoginStatus = 'idle' | 'error' | 'success';

const FALLBACK_LOGIN_ERROR = '账号或密码不正确';

function safeLoginError(reason: Error) {
  const message = reason.message.trim();
  if (
    message.length > 0 &&
    message.length <= 80 &&
    /[\u3400-\u9fff]/u.test(message) &&
    !/[<>]/u.test(message)
  ) {
    return message;
  }
  return FALLBACK_LOGIN_ERROR;
}

function CharacterEyes({ light = false }: { light?: boolean }) {
  return (
    <span className="character-eyes" aria-hidden="true">
      <i className={light ? 'character-eye character-eye-light' : 'character-eye'}>
        <b />
      </i>
      <i className={light ? 'character-eye character-eye-light' : 'character-eye'}>
        <b />
      </i>
    </span>
  );
}

function GeometricCharacters({
  activeField,
  passwordVisible,
  usernameGlance,
  status,
  stageRef,
}: {
  activeField: ActiveField;
  passwordVisible: boolean;
  usernameGlance: boolean;
  status: LoginStatus;
  stageRef: RefObject<HTMLDivElement | null>;
}) {
  return (
    <div
      ref={stageRef}
      className="character-stage"
      data-focus={activeField ?? 'none'}
      data-password-visible={String(passwordVisible)}
      data-glance={String(usernameGlance)}
      data-status={status}
      data-testid="character-stage"
      aria-hidden="true"
    >
      <div className="character-ground" />
      <div className="character character-purple">
        <CharacterEyes light />
        <span className="character-mouth" />
      </div>
      <div className="character character-charcoal">
        <CharacterEyes light />
        <span className="character-mouth" />
      </div>
      <div className="character character-orange">
        <CharacterEyes />
        <span className="character-mouth character-mouth-smile" />
      </div>
      <div className="character character-yellow">
        <CharacterEyes />
        <span className="character-mouth character-mouth-line" />
      </div>
    </div>
  );
}

export function LoginScreen({ onAuthenticated }: { onAuthenticated: (user: CurrentUser) => void }) {
  const reducedMotion = Boolean(useReducedMotion());
  const stageRef = useRef<HTMLDivElement>(null);
  const glanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const feedbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [activeField, setActiveField] = useState<ActiveField>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [usernameGlance, setUsernameGlance] = useState(false);
  const [status, setStatus] = useState<LoginStatus>('idle');
  const [error, setError] = useState('');

  const form = useForm<LoginInput>({
    resolver: zodResolver(loginRequestSchema),
    defaultValues: { username: '', password: '', rememberMe: true },
  });

  useEffect(() => {
    if (reducedMotion) return;
    const stage = stageRef.current;
    if (!stage) return;

    let pointerX = window.innerWidth / 2;
    let pointerY = window.innerHeight / 2;
    let animationFrame = 0;

    const resetGaze = () => {
      stage.style.setProperty('--eye-x', '0px');
      stage.style.setProperty('--eye-y', '0px');
    };

    const renderGaze = () => {
      animationFrame = 0;
      const bounds = stage.getBoundingClientRect();
      const centerX = bounds.left + bounds.width / 2;
      const centerY = bounds.top + bounds.height * 0.43;
      const normalizedX = Math.max(-1, Math.min(1, (pointerX - centerX) / (bounds.width * 0.52)));
      const normalizedY = Math.max(-1, Math.min(1, (pointerY - centerY) / (bounds.height * 0.72)));
      stage.style.setProperty('--eye-x', `${(normalizedX * 3.2).toFixed(2)}px`);
      stage.style.setProperty('--eye-y', `${(normalizedY * 2.5).toFixed(2)}px`);
    };

    const handlePointerMove = (event: PointerEvent) => {
      pointerX = event.clientX;
      pointerY = event.clientY;
      if (!animationFrame) animationFrame = window.requestAnimationFrame(renderGaze);
    };

    const handlePointerOut = (event: PointerEvent) => {
      if (event.relatedTarget === null) resetGaze();
    };

    const handleVisibility = () => {
      stage.dataset.paused = String(document.hidden);
      if (document.hidden) resetGaze();
    };

    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    window.addEventListener('pointerout', handlePointerOut, { passive: true });
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerout', handlePointerOut);
      document.removeEventListener('visibilitychange', handleVisibility);
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
    };
  }, [reducedMotion]);

  useEffect(
    () => () => {
      if (glanceTimerRef.current) clearTimeout(glanceTimerRef.current);
      if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    },
    [],
  );

  const mutation = useMutation({
    mutationFn: (input: LoginInput) => login(loginRequestSchema.parse(input)),
    onMutate: () => {
      setError('');
      setStatus('idle');
    },
    onSuccess: (user) => {
      setStatus('success');
      successTimerRef.current = setTimeout(() => onAuthenticated(user), reducedMotion ? 90 : 560);
    },
    onError: (reason: Error) => {
      setError(safeLoginError(reason));
      setStatus('error');
      if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
      feedbackTimerRef.current = setTimeout(() => setStatus('idle'), 520);
    },
  });

  const handleUsernameFocus = () => {
    setActiveField('username');
    setUsernameGlance(true);
    if (glanceTimerRef.current) clearTimeout(glanceTimerRef.current);
    glanceTimerRef.current = setTimeout(() => setUsernameGlance(false), 720);
  };

  const busy = mutation.isPending || status === 'success';

  return (
    <main className="login-page">
      <motion.section
        className="login-shell"
        data-status={status}
        initial={reducedMotion ? false : { opacity: 0, y: 12 }}
        animate={
          status === 'success'
            ? reducedMotion
              ? { opacity: 0 }
              : { opacity: 0, y: -18, scale: 0.99 }
            : { opacity: 1, y: 0, scale: 1 }
        }
        transition={{ duration: status === 'success' ? 0.42 : 0.32, ease: [0.22, 1, 0.36, 1] }}
      >
        <section className="login-visual-panel" aria-label="瑞峰选品品牌人物">
          <div className="login-brand">
            <span className="login-brand-icon" aria-hidden="true">
              <Plus size={17} strokeWidth={2.8} />
            </span>
            <strong>瑞峰选品</strong>
          </div>
          <GeometricCharacters
            activeField={activeField}
            passwordVisible={showPassword}
            usernameGlance={usernameGlance}
            status={status}
            stageRef={stageRef}
          />
        </section>

        <section className="login-form-panel">
          <div className="login-form-wrap">
            <div className="login-mobile-brand">
              <span className="login-brand-icon" aria-hidden="true">
                <Plus size={16} strokeWidth={2.8} />
              </span>
              <strong>瑞峰选品</strong>
            </div>
            <div className="login-form-mark" aria-hidden="true">
              <Plus size={22} strokeWidth={2.6} />
            </div>
            <header className="login-copy">
              <h1>欢迎回来</h1>
              <p>登录后继续选品与数据分析</p>
            </header>
            <form
              className="login-form"
              aria-busy={busy}
              onSubmit={form.handleSubmit((value) => mutation.mutate(value))}
            >
              <div className="login-field">
                <label htmlFor="login-username">账号</label>
                <Input
                  id="login-username"
                  className="login-input"
                  autoComplete="username"
                  placeholder="请输入账号"
                  disabled={busy}
                  {...form.register('username')}
                  onFocus={handleUsernameFocus}
                  onBlur={() => setActiveField(null)}
                />
                <small>{form.formState.errors.username ? '请输入至少 2 位账号' : ''}</small>
              </div>

              <div className="login-field">
                <label htmlFor="login-password">密码</label>
                <span className="login-password-field">
                  <Input
                    id="login-password"
                    className="login-input"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="请输入密码"
                    disabled={busy}
                    {...form.register('password')}
                    onFocus={() => setActiveField('password')}
                    onBlur={() => setActiveField(null)}
                  />
                  <button
                    type="button"
                    className="login-password-toggle"
                    aria-label={showPassword ? '隐藏密码' : '显示密码'}
                    aria-pressed={showPassword}
                    disabled={busy}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => setShowPassword((current) => !current)}
                  >
                    {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </span>
                <small>{form.formState.errors.password ? '请输入至少 6 位密码' : ''}</small>
              </div>

              <label className="remember-row">
                <input type="checkbox" disabled={busy} {...form.register('rememberMe')} />
                <span>保持登录 7 天</span>
              </label>

              <div className="login-error-slot" aria-live="polite" aria-atomic="true">
                {error ? <p className="form-error">{error}</p> : null}
              </div>

              <Button className="login-submit" type="submit" size="lg" disabled={busy}>
                {mutation.isPending ? (
                  <>
                    <Loader2 className="spin" size={17} /> 正在登录…
                  </>
                ) : status === 'success' ? (
                  <>
                    <Check size={17} /> 登录成功
                  </>
                ) : (
                  '进入工作台'
                )}
              </Button>
            </form>
          </div>
        </section>
      </motion.section>
    </main>
  );
}
